import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  XMarkIcon,
  UserGroupIcon,
  MapPinIcon,
  BriefcaseIcon,
  SparklesIcon,
  ShoppingCartIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  TableCellsIcon,
  PlusIcon,
  TrashIcon,
  PauseIcon,
  PlayIcon,
  EnvelopeIcon,
  // InboxIcon, // commented out – inbox feature disabled
} from '@heroicons/react/24/outline';
import CreditBuyModal from '../customer/components/CreditBuyModal';

export default function AIAgent() {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState('list');

  // ── Agent type and form state ──
  const [agentType, setAgentType] = useState('original'); // only 'original' is used now
  const [form, setForm] = useState({
    name: '',
    startDate: '',
    endDate: '',
    dailyBudget: '',
  });

  // ── Inbox monitoring specific – COMMENTED OUT (future feature) ──
  // const [availableInboxes, setAvailableInboxes] = useState([]);
  // const [selectedInboxIds, setSelectedInboxIds] = useState([]);
  // const [responseSpeed, setResponseSpeed] = useState('slow');

  // ── Other state ──
  const [icpData, setIcpData] = useState(null);
  const [loadingIcp, setLoadingIcp] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [locations, setLocations] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [error, setError] = useState('');
  const [feedback, setFeedback] = useState('');
  const [salesCredits, setSalesCredits] = useState(0);
  const [loadingCredits, setLoadingCredits] = useState(false);
  const [showBuyModal, setShowBuyModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savedAgents, setSavedAgents] = useState([]);

  // ── Fetch company profile (only for credits now – inbox part commented) ──
  const fetchCompanyProfile = useCallback(async () => {
    setLoadingCredits(true);
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/company', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch company profile');
      const payload = await response.json();
      const row = payload?.data?.[0] || payload?.data || {};
      setSalesCredits(Number(row.SalesCredits || 0));

      // ── Inbox EmailDetails – commented out ──
      // let emailDetails = [];
      // try {
      //   if (row.EmailDetails) {
      //     emailDetails = typeof row.EmailDetails === 'string'
      //       ? JSON.parse(row.EmailDetails)
      //       : (Array.isArray(row.EmailDetails) ? row.EmailDetails : []);
      //   }
      // } catch (e) {
      //   console.warn('Failed to parse EmailDetails:', e);
      // }
      // setAvailableInboxes(emailDetails);
      // setSelectedInboxIds([]);
    } catch (err) {
      console.error('Error fetching company profile:', err);
      setError('Unable to load company data.');
    } finally {
      setLoadingCredits(false);
    }
  }, []);

  // ── Fetch saved agents ──
  const fetchSavedAgents = async () => {
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/agent/save', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to fetch agents');
      const payload = await response.json();
      const agents = payload.data || payload.agents || [];
      setSavedAgents(agents);
      if (agents.length > 0) setView('list');
      else setView('form');
    } catch (err) {
      console.error('Error fetching agents:', err);
      setView('form');
    }
  };

  useEffect(() => {
    if (open) {
      fetchCompanyProfile();
      fetchSavedAgents();
    }
  }, [open, fetchCompanyProfile]);

  // ── Agent action handler ──
  const handleAgentAction = async (agentId, action) => {
    setError('');
    setFeedback('');

    if (agentId === undefined || agentId === null) {
      setError('Unable to identify this agent. Refresh the list and try again.');
      return;
    }

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/agent/actions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agentId, action }),
      });
      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Action failed');
      }
      await fetchSavedAgents();
      setFeedback(result.message || `Agent action completed successfully.`);
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to perform action.');
    }
  };

  // ── Calculate required credits – only for 'original' now ──
  const calculateRequiredCredits = () => {
    if (!form.startDate || !form.endDate) return null;
    const start = new Date(form.startDate);
    const end = new Date(form.endDate);
    if (isNaN(start) || isNaN(end) || end < start) return null;
    const days = Math.floor((end - start) / (1000 * 60 * 60 * 24)) + 1;

    // ── Inbox logic commented out ──
    // if (agentType === 'inbox') { ... }

    // Only original
    if (!form.dailyBudget || Number(form.dailyBudget) <= 0) return null;
    const daily = Number(form.dailyBudget);
    return days * daily;
  };

  const requiredCredits = calculateRequiredCredits();
  const canAfford = requiredCredits !== null && requiredCredits <= salesCredits;

  // Validation flags
  const startDateInvalid = form.startDate && new Date(form.startDate).setHours(0,0,0,0) <= new Date().setHours(0,0,0,0);
  const missingFields = !form.name || !form.startDate || !form.endDate ||
    (!form.dailyBudget || Number(form.dailyBudget) <= 0); // always need dailyBudget now

  // ── Inbox specific (commented out) ──
  // const inboxCount = selectedInboxIds.length;
  // const costPerInbox = responseSpeed === 'fast' ? 4 : 2;
  // const inboxDailyCost = inboxCount * costPerInbox;
  // const hasInboxes = availableInboxes.length > 0;

  // ── Load ICP ──
  const loadICP = async () => {
    if (missingFields) {
      setError('Please complete all required fields.');
      return;
    }
    if (startDateInvalid) {
      setError('Start date must be tomorrow or later.');
      return;
    }
    if (!canAfford) {
      setError('You do not have enough credits to run this agent.');
      return;
    }
    setLoadingIcp(true);
    setError('');
    setFeedback('');
    try {
      const response = await fetch('https://api.insightos.co.uk/leads/company', {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Failed to load company profile');
      const payload = await response.json();
      const row = payload?.data?.[0] || payload?.data || {};
      if (row.ICP) {
        const icp = JSON.parse(row.ICP);
        const aiResult = icp?.aiResult || {};
        setIcpData({
          industries: aiResult.industries || '',
          companySize: aiResult.companySize || '',
          locations: aiResult.locations || '',
          decisionMakers: aiResult.decisionMakers || '',
        });
        setFeedback('ICP loaded successfully.');
      } else {
        setIcpData(null);
        setFeedback('No ICP found. Please generate an ICP first.');
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to load ICP.');
    } finally {
      setLoadingIcp(false);
    }
  };

  // ── Generate Plan ──
  const generatePlan = async () => {
    if (missingFields) {
      setError('Please complete all required fields.');
      return;
    }
    if (startDateInvalid) {
      setError('Start date must be tomorrow or later.');
      return;
    }
    if (!canAfford) {
      setError('You do not have enough credits to run this agent.');
      return;
    }
    if (!icpData) {
      setError('Please load ICP first.');
      return;
    }
    setGenerating(true);
    setError('');
    setFeedback('');

    const prompt = `Industries: ${icpData.industries}\nCompany size: ${icpData.companySize}\nLocations: ${icpData.locations}\nDecision makers: ${icpData.decisionMakers}\nGenerate targeted business locations and sectors for outbound sales.`;

    try {
      const [locRes, secRes] = await Promise.all([
        fetch('https://api.insightos.co.uk/leads/ai/locations', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ prompt }),
        }),
        fetch('https://api.insightos.co.uk/leads/ai/sectors', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ prompt }),
        }),
      ]);

      const locPayload = await locRes.json();
      const secPayload = await secRes.json();

      if (!locRes.ok || !secRes.ok) {
        throw new Error(locPayload.error || secPayload.error || 'Failed to generate plan');
      }

      setLocations(locPayload.data || []);
      setSectors(secPayload.data || []);
      setFeedback('Plan generated successfully. Review and save your agent.');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to generate plan.');
    } finally {
      setGenerating(false);
    }
  };

  // ── Save Agent – only 'original' now ──
  const saveAgent = async () => {
    // Validate for original
    if (!locations.length || !sectors.length) {
      setError('Please generate a plan first.');
      return;
    }
    if (!canAfford) {
      setError('You do not have enough credits.');
      return;
    }

    setSaving(true);
    setError('');
    setFeedback('');

    const agentData = {
      locations,
      sectors,
      decisionMakers: icpData?.decisionMakers || '',
      name: form.name,
      startDate: form.startDate,
      endDate: form.endDate,
      creditLimit: Number(form.dailyBudget),
      agentType: 'original',
      // Inbox fields not sent
    };

    try {
      const response = await fetch('https://api.insightos.co.uk/leads/agent/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(agentData),
      });

      const result = await response.json();
      if (!response.ok || !result.success) {
        throw new Error(result.message || 'Failed to save agent');
      }

      setFeedback('Agent saved successfully!');
      // Reset form
      setForm({ name: '', startDate: '', endDate: '', dailyBudget: '' });
      setIcpData(null);
      setLocations([]);
      setSectors([]);
      await fetchSavedAgents();
      setView('list');
    } catch (err) {
      console.error(err);
      setError(err.message || 'Unable to save agent.');
    } finally {
      setSaving(false);
    }
  };

  const handleCreditPurchaseSuccess = () => {
    setShowBuyModal(false);
    fetchCompanyProfile();
  };

  // ── Helper to toggle selection in multi-select (commented out) ──
  // const handleInboxSelection = (e) => { ... };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-10 right-6 z-40 flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-xl ring-1 ring-black/5 transition-transform hover:scale-105"
        aria-label="Open AI Agent"
      >
        <img src="/ava.png" alt="AI Agent" className="h-10 w-10 rounded-full object-cover" />
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, y: 20 }}
              animate={{ scale: 1, y: 0 }}
              exit={{ scale: 0.9, y: 20 }}
              transition={{ type: 'spring', stiffness: 300, damping: 30 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-4xl max-h-[90vh] overflow-hidden rounded-3xl bg-white shadow-2xl"
            >
              {/* Header */}
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <div className="flex items-center gap-3">
                  <img src="/ava.png" alt="Agent" className="h-9 w-9 rounded-full" />
                  <h2 className="text-xl font-bold text-gray-900">
                    {view === 'form' ? 'Create New Agent' : 'Agents'}
                  </h2>
                </div>
                <div className="flex items-center gap-2">
                  {view === 'list' && (
                    <button
                      onClick={() => setView('form')}
                      className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                      title="Create new agent"
                    >
                      <PlusIcon className="h-6 w-6" />
                    </button>
                  )}
                  <button
                    onClick={() => setOpen(false)}
                    className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 hover:text-gray-900"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>
              </div>

              {/* Body */}
              <div className="max-h-[calc(90vh-73px)] overflow-y-auto">
                {view === 'form' ? (
                  <div className="space-y-6 p-6">
                    {/* ── Agent Type Selector (only Original) ── */}
                    <div>
                      <label className="mb-2 block text-sm font-semibold text-gray-700">Agent Type</label>
                      <div className="flex flex-wrap gap-3">
                        <button
                          onClick={() => setAgentType('original')}
                          className={`flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition bg-blue-600 text-white shadow`}
                        >
                          <EnvelopeIcon className="h-5 w-5" />
                          Original
                        </button>
                        {/* ── Inbox Monitor button – commented out ──
                        <button
                          onClick={() => setAgentType('inbox')}
                          className="flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition bg-gray-100 text-gray-700 hover:bg-gray-200"
                        >
                          <InboxIcon className="h-5 w-5" />
                          Inbox Monitor
                        </button>
                        */}
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Single initial email, 3 credits/lead, uses ICP.
                      </p>
                    </div>

                    {/* ── Common fields ── */}
                    <div className="grid gap-4 md:grid-cols-2">
                      <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700">
                          Agent Name
                        </label>
                        <input
                          type="text"
                          value={form.name}
                          onChange={(e) => setForm({ ...form, name: e.target.value })}
                          placeholder="e.g., UK SMB Hunter"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="mb-1 block text-sm font-semibold text-gray-700">
                            Start Date
                          </label>
                          <input
                            type="date"
                            value={form.startDate}
                            min={new Date(Date.now() + 86400000).toISOString().split('T')[0]}
                            onChange={(e) => setForm({ ...form, startDate: e.target.value })}
                            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                          {startDateInvalid && (
                            <p className="mt-1 text-xs text-red-500">Start date must be tomorrow or later.</p>
                          )}
                        </div>
                        <div>
                          <label className="mb-1 block text-sm font-semibold text-gray-700">
                            End Date
                          </label>
                          <input
                            type="date"
                            value={form.endDate}
                            min={form.startDate}
                            onChange={(e) => setForm({ ...form, endDate: e.target.value })}
                            className="w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                          />
                        </div>
                      </div>
                      {/* ── Daily Budget (always shown) ── */}
                      <div>
                        <label className="mb-1 block text-sm font-semibold text-gray-700">
                          Daily Budget (credits)
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={form.dailyBudget}
                          onChange={(e) => setForm({ ...form, dailyBudget: e.target.value })}
                          placeholder="e.g., 30"
                          className="w-full rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                        />
                        <p className="mt-1 text-sm text-gray-500">
                          Each lead costs 3 credits. You can enrich{' '}
                          <span className="font-semibold text-green-600">
                            {form.dailyBudget ? Math.floor(Number(form.dailyBudget) / 3) : 0}
                          </span> leads per day.
                        </p>
                      </div>
                    </div>

                    {/* ── Inbox specific fields – COMMENTED OUT ──
                    {agentType === 'inbox' && ( ... )}
                    */}

                    {/* ── Info message ── */}
                    <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
                      <p className="text-sm text-blue-800">
                        The agent works overnight and sends initial emails in the morning. Uses your ICP to find companies.
                      </p>
                    </div>

                    {/* ── Credit summary ── */}
                    {!missingFields && requiredCredits !== null && (
                      <div
                        className={`rounded-xl border p-4 ${
                          !canAfford ? 'border-red-200 bg-red-50' : 'border-green-200 bg-green-50'
                        }`}
                      >
                        <div className="flex flex-wrap items-center justify-between gap-4">
                          <div className="text-sm">
                            <p className="font-medium text-gray-700">
                              Available Credits:{' '}
                              {loadingCredits ? (
                                <span className="inline-flex items-center gap-1 text-gray-500">
                                  <ArrowPathIcon className="h-4 w-4 animate-spin" />
                                  Loading...
                                </span>
                              ) : (
                                <span className="font-bold text-gray-900">{salesCredits}</span>
                              )}
                            </p>
                            <p className="font-medium text-gray-700">
                              Required Credits: <span className="font-bold text-gray-900">{requiredCredits}</span>
                            </p>
                          </div>
                          {!canAfford && !loadingCredits && (
                            <button
                              onClick={() => setShowBuyModal(true)}
                              className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow hover:bg-emerald-400"
                            >
                              <ShoppingCartIcon className="h-4 w-4" />
                              Buy Credits
                            </button>
                          )}
                          {loadingCredits && (
                            <button
                              onClick={fetchCompanyProfile}
                              className="flex items-center gap-2 rounded-lg bg-gray-200 px-4 py-2 text-sm font-bold text-gray-800 hover:bg-gray-300"
                            >
                              <ArrowPathIcon className="h-4 w-4" />
                              Refresh
                            </button>
                          )}
                        </div>
                        {!canAfford && !loadingCredits && (
                          <p className="mt-2 text-sm font-semibold text-red-600">
                            You need {requiredCredits - salesCredits} more credits to run this agent.
                          </p>
                        )}
                      </div>
                    )}
                    {missingFields && (
                      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                        <p className="text-sm font-medium text-amber-800">
                          Please fill in all required fields (name, start date, end date, and daily budget).
                        </p>
                      </div>
                    )}

                    {/* ── ICP and Plan ── */}
                    <>
                      <div className="flex justify-end">
                        <button
                          onClick={loadICP}
                          disabled={loadingIcp || !canAfford || startDateInvalid || missingFields}
                          className="flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-semibold text-white shadow hover:bg-blue-500 disabled:opacity-50"
                        >
                          <UserGroupIcon className="h-4 w-4" />
                          {loadingIcp ? 'Loading...' : 'Load ICP'}
                        </button>
                      </div>

                      {icpData && (
                        <div className="mt-4">
                          <h3 className="mb-3 text-lg font-bold text-gray-900">Ideal Customer Profile</h3>
                          <div className="grid gap-3 sm:grid-cols-2">
                            {[
                              { label: 'Industries', value: icpData.industries },
                              { label: 'Company Size', value: icpData.companySize },
                              { label: 'Locations', value: icpData.locations },
                              { label: 'Decision Makers', value: icpData.decisionMakers },
                            ].map((item, idx) => (
                              <div key={idx} className="rounded-xl border border-gray-200 bg-gray-50 p-3">
                                <p className="text-xs font-bold uppercase tracking-wider text-gray-500">{item.label}</p>
                                <p className="mt-1 whitespace-pre-wrap text-sm text-gray-800">{item.value || '—'}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="flex justify-end">
                        <button
                          onClick={generatePlan}
                          disabled={!icpData || generating || !canAfford || startDateInvalid || missingFields}
                          className="flex items-center gap-2 rounded-lg bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-3 text-sm font-bold text-white shadow hover:from-blue-500 hover:to-indigo-500 disabled:opacity-50"
                        >
                          <SparklesIcon className="h-5 w-5" />
                          {generating ? 'Generating...' : 'Generate Plan'}
                        </button>
                      </div>

                      {(locations.length > 0 || sectors.length > 0) && (
                        <div className="space-y-6">
                          {locations.length > 0 && (
                            <div>
                              <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-gray-900">
                                <MapPinIcon className="h-5 w-5 text-blue-600" />
                                Locations
                              </h3>
                              <div className="flex flex-wrap gap-2">
                                {locations.map((loc, idx) => (
                                  <span
                                    key={idx}
                                    className="rounded-full bg-blue-100 px-3 py-1.5 text-sm font-medium text-blue-800"
                                  >
                                    {loc}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {sectors.length > 0 && (
                            <div>
                              <h3 className="mb-3 flex items-center gap-2 text-lg font-bold text-gray-900">
                                <BriefcaseIcon className="h-5 w-5 text-emerald-600" />
                                Sectors
                              </h3>
                              <div className="flex flex-wrap gap-2">
                                {sectors.map((sec, idx) => (
                                  <span
                                    key={idx}
                                    className="rounded-full bg-emerald-100 px-3 py-1.5 text-sm font-medium text-emerald-800"
                                  >
                                    {sec}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </>

                    {/* ── Save button ── */}
                    <div className="flex justify-end">
                      <button
                        onClick={saveAgent}
                        disabled={saving || !canAfford || !locations.length || !sectors.length || missingFields}
                        className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-bold text-white shadow hover:bg-emerald-400 disabled:opacity-50"
                      >
                        <CheckCircleIcon className="h-4 w-4" />
                        {saving ? 'Saving...' : 'Save Agent'}
                      </button>
                    </div>

                    {/* Feedback and errors */}
                    {feedback && (
                      <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
                        {feedback}
                      </div>
                    )}
                    {error && (
                      <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                        {error}
                      </div>
                    )}
                  </div>
                ) : (
                  /* ── List View ── */
                  <div className="p-6">
                    <h3 className="mb-4 flex items-center gap-2 text-lg font-bold text-gray-900">
                      <TableCellsIcon className="h-5 w-5 text-blue-600" />
                      Saved Agents
                    </h3>
                    <div className="overflow-x-auto rounded-xl border border-gray-200">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Type</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Active</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Name</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Start Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">End Date</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Frequency</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Credits Used</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Credit Limit / Day</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">AV Leads</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">AV Sent</th>
                            <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-200 bg-white">
                          {savedAgents.length > 0 ? (
                            savedAgents.map((agent, idx) => (
                              <tr key={idx} className="hover:bg-gray-50">
                                <td className="px-4 py-3 text-xs font-medium text-gray-600">
                                  {agent.AgentType === 'inbox' ? '📨 Inbox' : '📧 Original'}
                                </td>
                                <td className="px-4 py-3">
                                  {agent.Active === '1' || agent.Active === 1 ? (
                                    <span className="inline-block h-3 w-3 rounded-full animate-pulse bg-green-500" />
                                  ) : (
                                    <span className="inline-block h-3 w-3 rounded-full bg-red-300" />
                                  )}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">{agent.Name || '-'}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {agent.StartDate ? new Date(agent.StartDate).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {agent.EndDate ? new Date(agent.EndDate).toLocaleDateString() : '-'}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {agent.Frequency || (agent.AgentType === 'inbox' ? 'Inbox Monitor' : 'Daily')}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">{agent.CreditsUsed ?? 0}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">
                                  {agent.AgentType === 'inbox'
                                    ? `${agent.SelectedInboxes ? JSON.parse(agent.SelectedInboxes).length : 0} inboxes`
                                    : `${agent.CreditLimit ?? '-'} / ${Math.floor(agent.CreditLimit / 3)} Leads`}
                                </td>
                                <td className="px-4 py-3 text-sm text-gray-900">{agent.AVLeads || 'New'}</td>
                                <td className="px-4 py-3 text-sm text-gray-900">{agent.AVSent || 'New'}</td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-2">
                                    {agent.Active === '1' || agent.Active === 1 ? (
                                      <button
                                        onClick={() => handleAgentAction(agent.AgentId ?? agent.id, 'Deactivate')}
                                        className="flex items-center gap-1 rounded-md bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-200"
                                      >
                                        <PauseIcon className="h-3.5 w-3.5" />
                                        Deactivate
                                      </button>
                                    ) : (
                                      <button
                                        onClick={() => handleAgentAction(agent.AgentId ?? agent.id, 'Activate')}
                                        className="flex items-center gap-1 rounded-md bg-green-100 px-2 py-1 text-xs font-semibold text-green-700 hover:bg-green-200"
                                      >
                                        <PlayIcon className="h-3.5 w-3.5" />
                                        Activate
                                      </button>
                                    )}
                                    <button
                                      onClick={() => handleAgentAction(agent.AgentId ?? agent.id, 'Delete')}
                                      className="flex items-center gap-1 rounded-md bg-red-100 px-2 py-1 text-xs font-semibold text-red-700 hover:bg-red-200"
                                    >
                                      <TrashIcon className="h-3.5 w-3.5" />
                                      Delete
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          ) : (
                            <tr>
                              <td colSpan="11" className="px-4 py-4 text-center text-sm text-gray-500">
                                No agents saved yet.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {showBuyModal && (
        <CreditBuyModal
          onClose={() => setShowBuyModal(false)}
          onSuccess={handleCreditPurchaseSuccess}
        />
      )}
    </>
  );
}